import { UserIdentity } from '@backstage/core-components';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import {
  configApiRef,
  discoveryApiRef,
  githubAuthApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { Button, GithubIcon, Separator, Spinner } from '@vivicta/ui';
import { Building2, Lock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import wordmarkUrl from '../../assets/vivicta-wordmark.svg';
import { ThemeToggleButton } from '../theme/ThemeToggleButton';
import {
  GuestIdentity,
  rememberGuestSignIn,
  wasGuestSignIn,
} from './GuestIdentity';
import { PORTAL_CAPABILITIES, SignInBrandPanel } from './SignInBrandPanel';
import { SignInErrorAlert } from './SignInErrorAlert';

export const VivictaSignInPage = (props: SignInPageProps) => {
  const { onSignInSuccess } = props;
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  /* `auth.providers` is backend-only config, so the provider must be declared
   * separately under the frontend-visible `app.signInProvider` key. */
  const useGitHub =
    configApi.getOptionalString('app.signInProvider') !== 'microsoft';
  const providerTitle = useGitHub ? 'GitHub' : 'Microsoft';
  const providerMessage = useGitHub
    ? 'Continue with your Vivicta GitHub account. Access is limited to approved organization members.'
    : 'Continue with your Vivicta Microsoft account.';
  const ProviderIcon = useGitHub ? GithubIcon : Building2;
  const authApiRef = useGitHub ? githubAuthApiRef : microsoftAuthApiRef;

  const authApi = useApi(authApiRef);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | undefined>(
    errorParam ?? undefined,
  );
  const [busy, setBusy] = useState(false);
  const isDev = process.env.NODE_ENV !== 'production';
  const signInVerb = error ? 'Try again with' : 'Continue with';

  const completeSignIn = useCallback(async () => {
    const identityResponse = await authApi.getBackstageIdentity();
    if (!identityResponse) return;
    const profile = await authApi.getProfile();
    onSignInSuccess(
      UserIdentity.create({
        identity: identityResponse.identity,
        authApi,
        profile,
      }),
    );
  }, [authApi, onSignInSuccess]);

  /* Restore an existing session on mount, otherwise show the sign-in page */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isDev && wasGuestSignIn()) {
        try {
          const guest = await GuestIdentity.signIn(discoveryApi);
          if (!cancelled) onSignInSuccess(guest);
          return;
        } catch {
          rememberGuestSignIn(false);
        }
      }

      try {
        const existing = await authApi.getBackstageIdentity({ optional: true });
        if (!cancelled && existing) {
          const profile = await authApi.getProfile();
          onSignInSuccess(
            UserIdentity.create({
              identity: existing.identity,
              authApi,
              profile,
            }),
          );
          return;
        }
      } catch {
        /* no existing session — continue to show the sign-in page */
      }

      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [authApi, discoveryApi, isDev, onSignInSuccess]);

  const handleSignIn = useCallback(async () => {
    setBusy(true);
    setError(undefined);
    try {
      await authApi.getBackstageIdentity({ instantPopup: true });
      await completeSignIn();
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'PopupRejectedError') {
        setError(err.message);
      }
      /* Drop any half-established session so the next attempt is a clean flow */
      await authApi.signOut().catch(() => undefined);
      setBusy(false);
    }
  }, [authApi, completeSignIn]);

  /* Dev-only: signs in through the backend guest provider, so requests carry
   * a real token and the choice survives a reload. */
  const handleGuestSignIn = useCallback(async () => {
    setBusy(true);
    setError(undefined);
    try {
      const guest = await GuestIdentity.signIn(discoveryApi);
      rememberGuestSignIn(true);
      onSignInSuccess(guest);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }, [discoveryApi, onSignInSuccess]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner size={28} className="text-muted-foreground" />
        <span className="sr-only">Checking your session</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground lg:grid lg:grid-cols-2 xl:grid-cols-[1.15fr_1fr]">
      <div className="fixed right-4 top-4 z-10 sm:right-6 sm:top-6">
        <ThemeToggleButton />
      </div>

      <SignInBrandPanel />

      <main className="flex min-h-screen flex-col px-6 py-8 sm:px-10 lg:min-h-0 lg:px-12 lg:py-12 xl:px-20 xl:py-16">
        <div className="flex items-center justify-center gap-3 lg:hidden">
          <img src={wordmarkUrl} alt="Vivicta" className="h-5 w-auto" />
          {/* Mobile view wordmark */}
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Developer Portal
          </span>
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Sign in
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {providerMessage}
            </p>

            {error && <SignInErrorAlert error={error} />}
            {/* Sign in button */}
            <Button
              type="button"
              size="lg"
              className="mt-6 h-11 w-full"
              onClick={handleSignIn}
              disabled={busy}
            >
              {busy ? (
                <Spinner size={16} />
              ) : (
                <ProviderIcon aria-hidden="true" />
              )}
              {busy ? 'Signing in…' : `${signInVerb} ${providerTitle}`}
            </Button>

            {isDev && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="mt-3 h-11 w-full"
                onClick={handleGuestSignIn}
                disabled={busy}
              >
                Continue as Guest (dev only)
              </Button>
            )}

            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <Lock
                aria-hidden="true"
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              Single sign-on is required. Sessions are only issued to members of
              the Vivicta organisation.
            </p>

            <div className="lg:hidden">
              <Separator className="my-8" />
              <ul className="flex flex-col gap-3">
                {PORTAL_CAPABILITIES.map(({ icon: Icon, title }) => (
                  <li
                    key={title}
                    className="flex items-center gap-3 text-sm text-muted-foreground"
                  >
                    <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Need access? Ask the platform team to add you to the Vivicta
          organisation.
        </p>
      </main>
    </div>
  );
};
